import { useMemo, useState } from 'react';

import { ChevronDownIcon, Search, X } from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'components/Popover';
import { Input } from 'components/input';
import { Typography } from 'components/typography';
import { cn } from 'utils/shadcn';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  placeholder?: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  className?: string;
  disabled?: boolean;
  displayMode?: 'chips' | 'count';
}

export function MultiSelect({
  placeholder = 'Select...',
  value,
  onChange,
  options,
  className,
  disabled = false,
  displayMode = 'chips',
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue]);

  const selectedOptions = useMemo(() => {
    return options.filter((opt) => value.includes(opt.value));
  }, [options, value]);

  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleRemove = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  const renderSelected = () => {
    if (selectedOptions.length === 0) {
      return (
        <span className='text-neutral-input-placeholder'>{placeholder}</span>
      );
    }

    if (displayMode === 'count') {
      return <span>{`${selectedOptions.length} selected`}</span>;
    }

    return (
      <>
        {selectedOptions.slice(0, 2).map((opt) => (
          <span
            key={opt.value}
            className='inline-flex items-center gap-1 px-1.5 py-0.5 bg-neutral-100 text-neutral-700 rounded text-xs'
          >
            {opt.label}
            <button
              type='button'
              onClick={(e) => handleRemove(e, opt.value)}
              className='hover:bg-neutral-200 rounded'
              aria-label={`Remove ${opt.label}`}
            >
              <X className='h-3 w-3' />
            </button>
          </span>
        ))}
        {selectedOptions.length > 2 && (
          <span className='text-xs text-neutral-500'>
            +{selectedOptions.length - 2} more
          </span>
        )}
      </>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          disabled={disabled}
          className={cn(
            'flex h-8 w-full items-center justify-between rounded-[4px] border border-neutral-input-border bg-neutral-input-bg px-3 py-2 text-[13px] text-left ring-offset-neutral-50',
            'focus:outline-none focus:border-brand-main focus:ring-brand-200',
            'disabled:cursor-not-allowed disabled:bg-neutral-input-disabled-bg disabled:border-neutral-input-disabled-border disabled:text-neutral-input-disabled-text',
            value.length === 0
              ? 'text-neutral-input-placeholder'
              : 'text-neutral-input-text',
            className
          )}
        >
          <div className='flex-1 flex items-center gap-1 flex-wrap min-h-[20px]'>
            {renderSelected()}
          </div>
          <ChevronDownIcon className='h-4 w-4 opacity-50 shrink-0' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[var(--radix-popover-trigger-width)] p-0'
        align='start'
      >
        <div className='p-2 border-b border-neutral-200'>
          <div className='relative'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400' />
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder='Search...'
              className='pl-8 text-[13px]'
            />
          </div>
        </div>
        <div className='max-h-[300px] overflow-y-auto p-1'>
          {filteredOptions.length === 0 ? (
            <div className='px-2 py-4 text-center'>
              <Typography variant='body-sm' className='text-neutral-500'>
                No results found
              </Typography>
            </div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <div
                  key={option.value}
                  className='flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-neutral-100 cursor-pointer'
                  onClick={() => handleToggle(option.value)}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(option.value)}
                      className='shrink-0'
                    />
                  </div>
                  <Typography variant='body-sm' className='flex-1'>
                    {option.label}
                  </Typography>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
